import React, { useMemo, useEffect } from 'react';
import Table from '../../../../common/Table';
import { useFetch } from '../../../../../hooks/useCallApi';
import { Loader2 } from 'lucide-react';

const ComplaintActivitiesTab = ({ complaint }) => {
  const complaintId = complaint?.id;

  const {
    data: activitiesData,
    isLoading,
    error,
    refetch
  } = useFetch(
    ['complaint-activities', complaintId],
    (api) => api.admin.complaints.complaintActivities(complaintId),
    {
      enabled: !!complaintId,
      keepPreviousData: false,
      onSuccess: (response) => {
        console.log('✅ Activities fetched:', response);
      },
      onError: (err) => {
        console.error('❌ Error fetching activities:', err);
      },
    }
  );

  console.log("COMPLAIN ACTTT: ", activitiesData);

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    const stringText = String(text);
    if (stringText.length <= maxLength) return stringText;
    return stringText.substring(0, maxLength) + '...';
  };

  const activities = useMemo(() => {
    if (!activitiesData) return [];

    const logs = activitiesData?.data?.permit_application_logs || 
                 activitiesData?.permit_application_logs || 
                 activitiesData?.data || 
                 [];

    if (!Array.isArray(logs)) return [];

    return logs.map((log) => {
      let performedBy = 'System';
      
      if (log.user_name) {
        performedBy = log.user_name;
      }
      else if (log.user && log.user.name) {
        performedBy = log.user.name;
      }
      else if (log.user_id) {
        performedBy = `User ID: ${log.user_id}`;
      }
      else if (log.resident) {
        if (typeof log.resident === 'object' && log.resident.name) {
          performedBy = log.resident.name;
        } else if (typeof log.resident === 'string') {
          performedBy = log.resident;
        }
      }

      const formatValue = (value) => {
        if (!value) return '-';
        if (typeof value === 'object') {
          if (value.assignment?.assigned_at) {
            return `Assignment time updated`;
          }
          if (value.feedback) {
            return `Feedback: Rating ${value.feedback.rating || 'N/A'}`;
          }
          if (value.status) {
            return `Status changed to ${value.status}`;
          }
          if (value.category?.name) {
            return `Category: ${value.category.name}`;
          }
          return JSON.stringify(value);
        }
        return String(value);
      };

      const getChangedField = () => {
        if (log.new_values) {
          const keys = Object.keys(log.new_values);
          if (keys.includes('status')) return 'Status';
          if (keys.includes('assignment')) return 'Assignment';
          if (keys.includes('category')) return 'Category';
          if (keys.includes('description')) return 'Description';
          if (keys.includes('feedback')) return 'Feedback';
          return keys[0] || 'Complaint';
        }
        return log.entity_type || 'Complaint';
      };

      const getOldValue = () => {
        if (!log.old_values) return '-';
        
        if (log.old_values.status) {
          return log.old_values.status;
        }
        if (log.old_values.assignment?.assigned_at) {
          return 'Previous assignment';
        }
        if (log.old_values.category?.name) {
          return log.old_values.category.name;
        }
        if (log.old_values.description) {
          return log.old_values.description;
        }
        return formatValue(log.old_values);
      };

      const getNewValue = () => {
        if (!log.new_values) return '-';
        
        if (log.new_values.status) {
          return log.new_values.status;
        }
        if (log.new_values.assignment?.assigned_at) {
          return 'New assignment';
        }
        if (log.new_values.category?.name) {
          return log.new_values.category.name;
        }
        if (log.new_values.description) {
          return log.new_values.description;
        }
        if (log.new_values.feedback) {
          const feedback = log.new_values.feedback;
          return `Feedback: ${feedback.feedback || ''} ${feedback.rating ? `(Rating: ${feedback.rating})` : ''}`.trim();
        }
        return formatValue(log.new_values);
      };

      const rawOldValue = getOldValue();
      const rawNewValue = getNewValue();

      return {
        id: log.id || Math.random().toString(),
        action: log.action ? log.action.charAt(0).toUpperCase() + log.action.slice(1) : 'Update',
        field: getChangedField(),
        date: log.created_at || log.updated_at,
        old_value: rawOldValue,
        new_value: rawNewValue,
        old_value_truncated: truncateText(rawOldValue, 40),
        new_value_truncated: truncateText(rawNewValue, 40),
        user: performedBy,
        user_id: log.user_id,
        _raw: log
      };
    }).sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA;
    });
  }, [activitiesData]);

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return dateTimeString;
      
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateTimeString;
    }
  };

  const columns = [
    {
      key: 'action',
      label: 'Action',
      render: (value, row) => (
        <span className="text-sm font-medium text-gray-900">
          {row.action || 'N/A'}
        </span>
      )
    },
    {
      key: 'field',
      label: 'Field',
      render: (value, row) => (
        <span className="text-sm text-gray-600">
          {row.field || 'N/A'}
        </span>
      )
    },
    {
      key: 'date',
      label: 'Date & Time',
      render: (value, row) => (
        <span className="text-sm text-gray-500">
          {formatDateTime(row.date)}
        </span>
      )
    },
    {
      key: 'old_value',
      label: 'Old Value',
      render: (value, row) => {
        const displayValue = row.old_value_truncated || '-';
        const fullValue = row.old_value || '-';
        const isTruncated = displayValue !== fullValue && displayValue.endsWith('...');
        
        return (
          <div className="max-w-[200px]">
            <span 
              className="text-sm text-gray-500 block truncate" 
              title={isTruncated ? fullValue : undefined}
            >
              {displayValue}
            </span>
          </div>
        );
      }
    },
    {
      key: 'new_value',
      label: 'New Value',
      render: (value, row) => {
        const displayValue = row.new_value_truncated || '-';
        const fullValue = row.new_value || '-';
        const isTruncated = displayValue !== fullValue && displayValue.endsWith('...');
        
        return (
          <div className="max-w-[200px]">
            <span 
              className="text-sm text-gray-900 block truncate" 
              title={isTruncated ? fullValue : undefined}
            >
              {displayValue}
            </span>
          </div>
        );
      }
    },
    {
      key: 'user',
      label: 'Performed By',
      render: (value, row) => (
        <span className="text-sm text-gray-600">
          {row.user || 'System'}
        </span>
      )
    }
  ];

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg border border-gray-200">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
        <p className="text-sm text-gray-500">Loading activity logs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-sm text-gray-500 mb-4">Failed to load activity records</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <Table
          columns={columns}
          data={activities}
          noContainer={true}
          getRowKey={(row) => row.id}
          pagination={{
            currentPage: 1,
            lastPage: Math.max(1, Math.ceil(activities.length / 10)),
            perPage: 10,
            total: activities.length
          }}
          onPageChange={() => {}}
          paginationProps={{
            showSizeSelector: true,
            onPerPageChange: () => {}
          }}
        />
      </div>

      {activities.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-gray-500">No activity records found for this complaint</p>
        </div>
      )}
    </div>
  );
};

export default ComplaintActivitiesTab;